'use client';

import { ArrowUp, Minus, Plus, RotateCcw, type LucideIcon } from 'lucide-react';
import type { EstadoItemAdscripcion, ResultadoItem } from './adscripcionPreview';
import { TextoDeCambio } from './TextoDeCambio';

const ITEM_CHIP: Record<Exclude<EstadoItemAdscripcion, 'pendiente'>, { label: string; estilo: string }> = {
	aprobado: { label: 'aprobado', estilo: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
	rechazado: { label: 'rechazado', estilo: 'border-red-200 bg-red-50 text-red-700' },
};

/** Icon and colour of each kind of change, read from the item id (`principal`, `agregar-…`, `quitar-…`, `conservar-…`). */
const ICONO_DE_CAMBIO: { prefijo: string; Icono: LucideIcon; estilo: string }[] = [
	{ prefijo: 'principal', Icono: ArrowUp, estilo: 'bg-sky-50 text-sky-700 ring-sky-200' },
	{ prefijo: 'agregar-', Icono: Plus, estilo: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
	{ prefijo: 'quitar-', Icono: Minus, estilo: 'bg-red-50 text-red-600 ring-red-200' },
	{ prefijo: 'conservar-', Icono: RotateCcw, estilo: 'bg-amber-50 text-amber-700 ring-amber-200' },
];

function IconoDeCambio({ id }: { id: string }) {
	const tipo = ICONO_DE_CAMBIO.find((candidato) => id.startsWith(candidato.prefijo));
	if (!tipo) return <span aria-hidden="true" />;
	const { Icono, estilo } = tipo;
	// 16px: the height of the first text line (text-xs, 1rem), so the icon sits on it and never between two lines. A
	// 10px glyph leaves 3px on every side (whole pixels: no blurry, off-centre half pixel). Only simple strokes read at
	// this size: the principal is an arrow up ("moves up to principal"), not a star, which turns into a blot.
	return (
		// The cell is as tall as the first row (20px, the height of a status chip) and centres the circle in it, so the
		// icon lines up with the text whether or not the row carries a chip.
		<span aria-hidden="true" className="flex h-5 items-center">
			<span className={`flex h-4 w-4 items-center justify-center rounded-full ring-1 ring-inset ${estilo}`}>
				<Icono size={10} strokeWidth={2.5} className="block" />
			</span>
		</span>
	);
}

/**
 * Items of a combined adscription request. Each change opens with a small coloured icon (↑ principal, + add, − remove,
 * ↺ keep) and its text with the verb in bold; with `conMotivos` (always in the admin view) the reason the researcher
 * gave for THAT change sits right under it, dimmed and without a label: the icon marks where each pair starts. The
 * resolved ones carry an "aprobado" / "rechazado" chip (the text is always there, colour only reinforces it) and the
 * reason of a rejection. `detalleAdmin` also adds a "Nueva" tag on a principal that is also new to the researcher.
 * Shared by the researcher's "Mis solicitudes" cards and the admin request screens.
 */
export function ItemsDeAdscripcion({
	items,
	detalleAdmin = false,
	conMotivos = false,
}: {
	items: ResultadoItem[];
	detalleAdmin?: boolean;
	conMotivos?: boolean;
}) {
	if (items.length === 0) return null;
	const motivos = conMotivos || detalleAdmin;
	return (
		<ul className={`mt-3 border-t border-slate-100 pt-3 ${motivos ? 'space-y-2.5' : 'space-y-1.5'}`} aria-label="Cambios solicitados">
			{items.map((item) => (
				<li key={item.id} className="grid grid-cols-[16px_minmax(0,1fr)] gap-x-2.5 text-xs leading-4 text-slate-700">
					<IconoDeCambio id={item.id} />
					<div className="min-w-0">
						<div className="flex min-h-5 flex-wrap items-center gap-x-2 gap-y-1">
							<span>
								<TextoDeCambio texto={item.texto} verboEnColor={false} />
							</span>
							{detalleAdmin && item.nueva ? (
								<span className="inline-block whitespace-nowrap rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[0.7rem] font-medium text-sky-700">
									Nueva
								</span>
							) : null}
							{item.estado !== 'pendiente' ? (
								<span
									className={`inline-block whitespace-nowrap rounded-full border px-2 py-0.5 text-[0.7rem] font-medium ${ITEM_CHIP[item.estado].estilo}`}
								>
									{ITEM_CHIP[item.estado].label}
								</span>
							) : null}
							{item.estado === 'rechazado' && item.motivo ? <span className="text-slate-500">· {item.motivo}</span> : null}
						</div>
						{motivos && item.motivoSolicitud ? (
							<p className="mt-0.5 whitespace-pre-line text-[0.7rem] leading-relaxed text-slate-500">{item.motivoSolicitud}</p>
						) : null}
					</div>
				</li>
			))}
		</ul>
	);
}
